import axiosLib, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

interface RequestDedupeMeta {
  __dedupeKey?: string;
  __dedupeController?: AbortController;
}

const pendingRequestDedupeByKey = new Map<string, AbortController>();

export function buildAxiosRequestDedupeKey(config: InternalAxiosRequestConfig): string | null {
  if (config.signal) {
    return null;
  }
  const method = (config.method ?? 'get').toUpperCase();
  const uri = axiosLib.getUri(config);
  const raw = config.data;
  let dataKey = '';
  if (raw !== undefined && raw !== null) {
    if (typeof raw === 'string') {
      dataKey = raw;
    } else if (typeof FormData !== 'undefined' && raw instanceof FormData) {
      dataKey = '[form-data]';
    } else {
      try {
        dataKey = JSON.stringify(raw);
      } catch {
        dataKey = '[non-serializable]';
      }
    }
  }
  return `${method}:${uri}:${dataKey}`;
}

export function clearAxiosRequestDedupeEntry(config: InternalAxiosRequestConfig): void {
  const meta = config as InternalAxiosRequestConfig & RequestDedupeMeta;
  const key = meta.__dedupeKey;
  const controller = meta.__dedupeController;
  if (
    key !== undefined &&
    controller !== undefined &&
    pendingRequestDedupeByKey.get(key) === controller
  ) {
    pendingRequestDedupeByKey.delete(key);
  }
}

/**
 * Supersede overlapping identical in-flight requests (same method, URL, body); latest wins.
 * Skipped when `config.signal` is already set (caller-owned cancellation).
 */
export function registerAxiosRequestDedupeInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (config.signal) {
        return config;
      }
      const key = buildAxiosRequestDedupeKey(config);
      if (key === null) {
        return config;
      }
      pendingRequestDedupeByKey.get(key)?.abort();
      const controller = new AbortController();
      pendingRequestDedupeByKey.set(key, controller);
      const meta = config as InternalAxiosRequestConfig & RequestDedupeMeta;
      meta.__dedupeKey = key;
      meta.__dedupeController = controller;
      config.signal = controller.signal;
      return config;
    },
    (error: AxiosError) =>
      Promise.reject(error instanceof Error ? error : new Error(String(error))),
  );
}
